/**
 * 流水契约 —— 记一笔 / 编辑 / 批量 / 筛选 / 视图（**C 域** · 架构 §6.1）
 *
 * 依据：C 域 PRD **§5.1**（行集与展示骨架）· **§5.1.4**（10 维筛选）· **§5.1.5**（批量分级授权）·
 * **§5.1.6**（软删 30 天）· **§5.1.9**（合计条）· A 域 PRD §5.1 / §5.2（腿模型与资金事件映射）·
 * 架构 **§4.1**（交易不持方向与金额）· **§6.1**（契约唯一源）· **§6.4**（分页与检索）·
 * `DR-004`（多币种）· `DR-005` / `DR-016` §7（冲减腿）· `DR-008`（账户 8 类）· `DR-034`（来源维度载体）。
 *
 * ## 🔴 三条"契约层就该堵死"的东西
 *
 * 1. **金额只收「整数分」且必须为正** —— 规格 §22.9「绝不用负数 `out`」；退款 / 理赔一律走
 *    **`in` 腿 + 支出分类**（冲减腿）。契约层用 `.positive()` 把负数挡在门外，
 *    比让负数进库、再由支出内核 `throw` 更早也更便宜。
 * 2. **交易不持方向与金额**（架构 §4.1）—— 所以「记一笔」的请求体里**没有** `amount` / `direction`
 *    顶层字段，只有 `legs[]`。这条限制看似啰嗦，却正是"同一笔钱只有一个记录处"的载体。
 * 3. **批量动作按交易结构分级授权**（`AC-C1.19`/`C1.20`）—— 契约层**不给**"万能批量"的写法：
 *    改分类 / 改账户与加标签 / 删除是**不同动作**，服务端据此对多腿交易直接拒绝（带原因）。
 *
 * ## 关于「派生形态」的一种"唯一表达"约定
 *
 * `parent`（父行）与 `[hasRefundLeg]` / `[hasUncategorizedLeg]` / `source` 都是**派生量**：
 * 服务端算好随行下发，**端侧不再自行判定**（架构 §5.2 手段 2「接口层只暴露口径结果」）。
 * 它们**不落库**（`AC-C1.3` 用数据审计钉住这一点）。
 */

import { z } from 'zod';
import { ListConfig, TransactionPolicy } from '../constants/index.js';
import { CategoryKind } from '../enums/platform.js';
import { LegDirection, TransactionStatus } from '../enums/journal.js';
import type { ParentRow } from '../nucleus/txn-row.js';
import type { AttributionView } from './contribution.js';
import { DayString } from './pagination.js';

// ═══════════════════════ 维度取值集（契约字段的取值集，不新开 enums/） ═══════════════════════
//
// ⚠️ 与 `JoinStrategy` 同例（见 `collaboration.ts`）：这些是**筛选维度的取值集**，
// 不是"两端各可能写一份"的枚举 ⇒ 就地定义在契约层即可。新增共享枚举须同时改架构 §4.2
// 与 `P域PRD` §3（`AC-P1.15`）。

/**
 * 筛选维度 7「方向」的取值（C 域 §5.1.4）。
 *
 * 🔴 **实现层细化（本批登记）**：PRD 给的是四个**中文语义标签**，未规定 SQL 判定表达式。
 * 数据库**执行不了** TS 纯函数（`deriveParentRow`），而把形态判定在 SQL 里重写一遍
 * 就是本项目最忌的「同一机制两份实现」。⇒ 本批采**腿级可索引**表达（与 PRD 用词一致 —— "方向"本就是腿的属性）：
 *
 * | 值 | 判定（腿级） | 直觉对齐 |
 * |---|---|---|
 * | `income` | 存在**计入收支**的 `in` 腿 | "这笔有收入" |
 * | `expense` | 存在**计入收支**的 `out` 腿 | "这笔有支出" |
 * | `transfer` | **全部腿都不计收支** 且 账户数 ≥ 2 | "这笔是资金搬运" |
 * | `excluded` | 存在**不计收支**的腿 | "这笔有钱在动但不算收支" |
 *
 * ⚠️ 四值按上表**可以同时命中同一笔**（如工资条：既有收入腿也有支出腿）。
 * 本维度是**单选**，用户每次只表达一个意图，重叠不影响可用性。
 * 若将来要求严格互斥的"父行形态四分"，须由派生层承担（登记见 changelog）。
 */
export const TxnDirectionFilter = {
  Income: 'income',
  Expense: 'expense',
  Transfer: 'transfer',
  Excluded: 'excluded',
} as const;
export type TxnDirectionFilter = (typeof TxnDirectionFilter)[keyof typeof TxnDirectionFilter];

/**
 * 筛选维度 8「状态」的取值（C 域 §5.1.4 / `AC-C1.15`）。
 *
 * ## 🔴 `deleted` 是**可见性开关**，不是"第四个过滤条件"（本批实现层细化，已登记）
 *
 * 这是本维度唯一一处**刻意偏离**「维度内 OR」的地方：
 *
 * | 写法 | 选中「已删除」时 | 结果 |
 * |---|---|---|
 * | 按 OR 处理（`deletedAt IS NULL` 被取消） | 已删 + 未删**混在一起** | ❌ 既不是回收站、也不是"未分类"，对用户是无意义的一坨 |
 * | 🔴 **按开关处理**（本实现） | **只看软删行** | ✅ 正是 §5.1.6 要的「状态 → 已删除即回收站」 |
 *
 * 判据是**语义性质**：前三个值回答"这些行**满足什么条件**"，`deleted` 回答"**哪些行进入结果集**"。
 * 把两种性质混在一个 OR 里没有正确的读法。其余三值仍按「维度内 OR」组合。
 */
export const TxnStatusFilter = {
  Uncategorized: 'uncategorized',
  Unpaid: 'unpaid',
  Refund: 'refund',
  /** 🔴 只看已软删的行（回收站）；**选中它时"未删"的行不会出现** */
  Deleted: 'deleted',
} as const;
export type TxnStatusFilter = (typeof TxnStatusFilter)[keyof typeof TxnStatusFilter];

/**
 * 筛选维度 9「来源」的取值（C 域 §5.1.4 / `AC-C1.16`）。
 *
 * | 值 | 载体 |
 * |---|---|
 * | `manual` | `sourceRuleId` 与 `importBatchId` **皆空**，且无 `ai_samples` 指向 |
 * | `import` | `importBatchId` 非空 |
 * | `schedule` | `sourceRuleId` 非空 |
 * | `ai` | 存在 `ai_samples.transaction_id` 指向本交易且 `accepted = 1` |
 *
 * 🔴 第 4 值的载体由 **`DR-034`** 裁定（`ai_samples` 增列 `transactionId`）：E 域红线⑤
 * 规定「AI 绝不直接落库、用户确认才入库」⇒ AI 快记在**事务数据上与手工记账不可区分**，
 * 必须由 AI 侧额外留下指向产物的关联，否则本维度尾值无源、M3 占比指标也算不出来。
 */
export const TxnSourceFilter = {
  Manual: 'manual',
  Import: 'import',
  Schedule: 'schedule',
  Ai: 'ai',
} as const;
export type TxnSourceFilter = (typeof TxnSourceFilter)[keyof typeof TxnSourceFilter];

// ═══════════════════════════ 写入（记一笔 / 编辑） ═══════════════════════════

/**
 * 腿金额入参 —— 🔴 **整数「分」且 > 0**。
 *
 * 上界由 `money.assertSafeInteger` 在写入前守住（安全整数），契约层不再重复声明一个数字。
 */
const AmountInput = z
  .number({ message: '金额必须是数字（整数「分」）' })
  .int('金额必须是整数「分」（规格 §22.1 全链路整数分）')
  .positive('金额必须大于 0 —— 🔴 退款 / 理赔请用 in 腿（冲减腿），不得用负数 out（规格 §22.9）');

/** 入参里的币种**不允许出现** —— 腿币种继承账户（架构 §4.1），由服务端按 `accountId` 解析 */
export const LegInputSchema = z.object({
  accountId: z.string().trim().min(1, '账户不得为空'),
  direction: z.enum([LegDirection.In, LegDirection.Out]),
  amount: AmountInput,
  /** `null` / 省略 = 未分类 */
  categoryId: z.string().trim().min(1).nullish(),
  note: z.string().trim().max(TransactionPolicy.NoteMaxLength).nullish(),
  /**
   * 🔴 **腿级「不计收支」** —— 必须存在，不能只靠交易级。
   *
   * 工资条（`DR-016` §3.3）里**同一笔交易内**同时有计入收支的腿（收入 / 社保缴费 / 个税）
   * 与不计收支的腿（医保 / 公积金 = 转账子项）⇒ 只有腿级标记能表达。
   * 判定时的有效值 = **本标记 ∨ 交易级标记**（见 `isCountedInStats`）。
   */
  excludeFromStats: z.boolean().optional(),
  /**
   * 🔴 **腿级笔级优惠（`discount`）** —— 整数分、`≥ 0`、缺省 `0`（`DR-040` 语义 · `DR-042` D7 实施）。
   *
   * `amount` **恒为实付净额**；「原价」= `amount + discount`（**派生、不落库**）。
   * 🔴 **不参与任何聚合**（余额 / 支出内核 / 预算 / 报表 / 账单 / 净资产一律只看 `amount`）。
   * ⚠️ **仅支出腿**（`direction = 'out'`）可为非 0；`in` 腿传非 0 ⇒ 服务端拒绝（`AC-C2.34`）。
   */
  discount: z.number().int().nonnegative().optional(),
  /**
   * 🆕 **重大资产归因**（R 域 `DR-053` D12 · 批 91）。
   *
   * 用途：重大资产的**资金动作**（购入 / 追加 / 收益 / 处置）生成的腿挂它，
   * 回报四数从腿现算，**业务表不落金额副本**（`AC-R6.6`）。
   *
   * 🔴 归属由**服务端**校验：只接受**本账本**、**未处置**的资产 id
   * （跨账本引用会让两个账本的回报都错）。契约只做形状。
   */
  linkedMajorAssetId: z.string().trim().min(1).nullish(),
  /**
   * 🆕 **还款优惠归因**（B 域 `DR-061` · 批 106-B）。
   *
   * 用途：一次「还款 + 优惠」（减免 / 银行返现 / 积分抵扣）里，优惠那一腿挂它，
   * 使优惠**落腿**（1 腿 `in` 到信用卡 · 无分类 · `excludeFromStats` · 本列非空），
   * 否则卡面会残留 −W 的假欠款（`AC-B2.46`）。
   *
   * 🔴 **还款腿四类互斥归属**（按序判定，禁 note 文案判据）：
   * 优惠（本列非空）→ 退款（`isRefundTxn`）→ 分期本金
   * （`transaction_id ∈ loan_schedules.paid_transaction_id`）→ 其余还款。
   *
   * 🔴 归属由**服务端**校验：只接受**本账本**的优惠 id。归因列**不设 FK**
   * （与 `linkedMajorAssetId` 同处置：软删交易后优惠表行独立保留）。
   */
  linkedWaiverId: z.string().trim().min(1).nullish(),
});
export type LegInput = z.infer<typeof LegInputSchema>;

/**
 * 一笔交易最多几条腿（**防御性上限，非产品口径**）。
 *
 * 依据量级：工资条子项上限 15（`DR-016` §3.8④），其中"转账"类子项各占 2 条腿
 * ⇒ 真实上界约 `15 × 2 = 30`。此处取 64 是为留余量，**不构成业务约束**。
 */
const MaxLegsPerTxn = 64;

/** 标签 id 列表（每笔上限 5 —— `ListConfig.MaxTagsPerTransaction`，`AC-C4.4`） */
const TagIdList = z
  .array(z.string().trim().min(1, '标签 id 不得为空'))
  .max(ListConfig.MaxTagsPerTransaction, `每笔标签上限 ${ListConfig.MaxTagsPerTransaction} 个`);

/**
 * 🆕 **工资条的录入口径**（C 域 · 批 141 · `DR-016` §3.6 / C 域 PRD §5.2.1⑤）
 *
 * | 值 | 含义 | 腿结构 |
 * |---|---|---|
 * | `'simple'` | 只记**到手的钱**（一条工资收入），**未含社保与个税** | 1 条 `in` + 工资分类 |
 * | `null`（缺省） | 普通交易 / **完整工资条** | 完整工资条是多腿，结构本身可辨 |
 *
 * 🔴 为什么"完整"不需要一个值：多腿 + 跨账户 + 含代扣**就是事实本身**，
 * 再写一个 `'full'` 是同一事实的第二份载体（铁律 24）。
 *
 * 🔴 为什么"简化"必须落库：两种口径的**净额都等于实发**（完整模式 `Σin−Σout`、
 * 简化模式就一条 `in`）⇒ 事后**无法从腿反推**。而 `DR-016` §3.6 要求
 * 年度税负处提示「数据不完整」—— 没有这个标记就没有判据。
 */
export const SALARY_MODE_SIMPLE = 'simple' as const;
export type SalaryMode = typeof SALARY_MODE_SIMPLE;

/**
 * `POST /api/transactions` —— **记一笔**（全形态单一入口）
 *
 * 覆盖 A 域 §5.2 的资金事件映射表：收入 / 支出（1 腿）、转账（2 腿，含跨币种）、
 * 工资条与拆分（多腿）。🔴 **本接口是唯一的流水写入入口** ——
 * AI 快记（E 域）、账单导入（D 域）、周期生成（F 域）**最终都走它**，
 * 不得各自 `insert`（那会让"合法性校验"出现第二、第三份）。
 *
 * ⚠️ 下列规则**契约层表达不了**，由服务端在拥有账户数据后校验（`transaction-write.service.ts`）：
 * 账户与分类**必须属于本账本**；**跨币种转账两腿均须 `excludeFromStats`**（`DR-004`）；
 * 分类 `kind` 与腿方向的一致性（收入分类不挂 `out` 腿的语义校验）。
 */
export const CreateTransactionRequestSchema = z.object({
  /** `YYYY-MM-DD`（账本本地日历日）—— 🔴 交易日是**日历事实**，无时区（架构 §4.5） */
  date: DayString,
  note: z.string().trim().max(TransactionPolicy.NoteMaxLength).nullish(),
  legs: z.array(LegInputSchema).min(1, '一笔交易至少 1 条腿').max(MaxLegsPerTxn),
  tagIds: TagIdList.optional(),
  /** 整笔不进统计（如本金还款、转账整笔）—— 与腿级标记取 **OR**（见 `isCountedInStats`） */
  excludeFromStats: z.boolean().optional(),
  /** 周期待缴费用为 `pending`（规格 §18）；缺省 `settled` */
  status: z.enum([TransactionStatus.Pending, TransactionStatus.Settled]).optional(),
  /**
   * 🆕 **工资条录入口径**（批 141）。`'simple'` = 只记到手、未含社保与个税。
   * 缺省 = 普通交易 / 完整工资条。见 {@link SALARY_MODE_SIMPLE} 的说明与两条理由。
   */
  salaryMode: z.literal(SALARY_MODE_SIMPLE).nullish(),
  /**
   * 🔴 **父交易**（`transactions.parentId`）—— `DR-035` v1.1 改判后**只剩「退款」一个用途**。
   *
   * | 用途 | 谁挂它 | 约束 |
   * |---|---|---|
   * | **退款**（冲减原消费） | 退款那笔 → 被退的原交易 | 本笔须是**退款形态**（`in` 腿 + 挂支出分类）；目标须是本账本一笔**未软删的支出**（净额为流出）；**退款不可再被冲减**（目标本身不得是退款） |
   * | **报销型理赔** | **可不挂**（C 域 PRD §5.2.3 明文） | — |
   *
   * 🔴 **拆分不再走这里**：拆分 = **同一笔交易的多条腿**（同账户 / 同向 / 各带分类），
   * 在建交易时与其它腿一起提交。原先的「拆分容器子项 → 父行」用途已随 `DR-035` v1.1 **删除**
   * —— 让容器与子项都带真实腿会让余额与统计**重复计一次**（实测：100 元支出扣了 200 元）。
   *
   * ⚠️ **本批无法强制「退款必挂 `parentId`」**：退款与报销型理赔在数据上**同形**
   * （都是「`in` 腿 + 支出分类」），而 PRD 又明说理赔可不挂 ⇒ 硬要求会把合法理赔拦掉。
   * 定稿 = 退款「**应挂**」（前端引导，服务端不拦）—— 见 `DR-035` v1.1 §5。
   */
  parentId: z.string().trim().min(1, '父交易 id 不得为空').nullish(),
  /** 储蓄目标归属（进度由腿派生，`savings_goals` 不存进度） */
  goalId: z.string().trim().min(1).nullish(),
  /**
   * 🔴 **周期生成溯源**（F 域 F1 · 规格 §18）—— 幂等键 `(sourceRuleId, sourcePeriodIndex)` 的前半。
   *
   * 唯一索引 `uq_txn_source_period`（`transactions` 表）兜底：
   * 同一规则同一期次**只能有一条**（`AC-F1.6`）⇒ 重复 tick / 重复点「立即生成」/ 重启补跑
   * 都不产生第二笔。手动记账**不传**该字段（`NULL` 不参与唯一性，故与手动记账互不干扰）。
   *
   * ⚠️ **两字段必须成对**（只给一个 ⇒ 规则侧数据不完整，服务端拒绝）。
   */
  sourceRuleId: z.string().trim().min(1, '规则 id 不得为空').nullish(),
  sourcePeriodIndex: z.number().int('期次序号必须是整数').nullish(),
  /**
   * 🆕 **记账时分期**（`DR-067` · 批 `116`）—— 信用卡消费当场分 N 期。
   *
   * | 项 | 谁定 |
   * |---|---|
   * | 期数 / 手续费方式 / 手续费额（本例三键） | **用户** |
   * | 🔴 本金 | **服务端**（= 本笔金额，`DR-067` D2①） |
   * | 🔴 首期账单日 | **服务端**（= 严格晚于记账日的最近账单日，`DR-067` D2②） |
   *
   * ⇒ 契约里**刻意没有** `principalTotal` / `startBillDay`：用户没有"另填一个本金"的入口，
   * 就不存在「本金 ≠ 消费额」的对账缺口。
   *
   * 🔴 **可选**（缺省 = 与今天完全一致的普通记账）。出现时必须满足：单笔 `out` 腿 +
   * 账户 `type='credit'` + 非退款 —— 否则 400 并给中文原因（`DR-067` D2④）。
   */
  installment: z
    .object({
      /** 期数（`E4`：2–60，与分期页同一区间） */
      periods: z
        .number({ message: '期数必须是数字' })
        .int()
        .min(2, '分期至少 2 期')
        .max(60, '分期最多 60 期'),
      /** 手续费录入方式（与 `POST /api/installments` 同义：`total` 总额 / `per_period` 每期） */
      feeMode: z.enum(['total', 'per_period']),
      /** 与 `feeMode` 配套的手续费金额（分，`0` = 免息；`E5`） */
      feeValue: z
        .number({ message: '手续费必须是数字（整数「分」）' })
        .int('手续费必须是整数「分」（规格 §22.1）')
        .nonnegative('手续费不得为负'),
    })
    .strict()
    .optional(),
});
export type CreateTransactionRequest = z.infer<typeof CreateTransactionRequestSchema>;

/**
 * `PATCH /api/transactions/:id` —— 编辑一笔。
 *
 * 🔴 字段语义是**整字段替换**不是"打补丁"：传了 `legs` 就是**整体替换腿集合**
 * （新增 / 删腿 / 改金额都走这一条），传了 `tagIds` 就是**整体替换标签集合**。
 * 理由：若做成"按 id 局部改腿"，编辑规则就要在契约层长出一套腿匹配逻辑 ——
 * 而 UI 侧本来就是"整张表单提交"（`AC-C1.34`：编辑复用「记一笔」同一套组件）。
 */
export const UpdateTransactionRequestSchema = z.object({
  date: DayString.optional(),
  note: z.string().trim().max(TransactionPolicy.NoteMaxLength).nullish(),
  legs: z.array(LegInputSchema).min(1, '一笔交易至少 1 条腿').max(MaxLegsPerTxn).optional(),
  tagIds: TagIdList.optional(),
  excludeFromStats: z.boolean().optional(),
  status: z.enum([TransactionStatus.Pending, TransactionStatus.Settled]).optional(),
  /** 🆕 工资条录入口径（见创建接口同名字段）；`null` = 清掉标记 */
  salaryMode: z.literal(SALARY_MODE_SIMPLE).nullish(),
  /** 🔴 `null` = **解除退款关联**（本笔仍是退款，只是不再指向原交易）；`undefined` = 不改。见创建接口的同名字段说明 */
  parentId: z.string().trim().min(1).nullish(),
  goalId: z.string().trim().min(1).nullish(),
});
export type UpdateTransactionRequest = z.infer<typeof UpdateTransactionRequestSchema>;

// ═══════════════════════════════ 批量 ═══════════════════════════════

/** 批量 id 列表 —— 🔴 **上限 200**（`AC-C1.22`；超限**报 400 不截断**，同分页上限的处置） */
const IdList = z
  .array(z.string().trim().min(1, '交易 id 不得为空'))
  .min(1, '至少要选中 1 笔')
  .max(TransactionPolicy.MaxBatchSize, `批量上限 ${TransactionPolicy.MaxBatchSize} 笔，请分批操作`);

/**
 * 批量动作（C 域 §5.1.5 的四个格子去掉"删除"—— 删除有独立入口，见 {@link BulkDeleteRequestSchema}）。
 *
 * 🔴 **分级授权不在契约层**（契约层不该知道"这笔是几腿"），但**动作粒度**在这里就分开了：
 * `setCategory` / `setAccount` 是**结构敏感**动作（只对单腿交易放行），
 * `addTags` / `removeTags` 是**结构无关**动作（多腿交易也放行）。
 */
export const BulkAction = {
  SetCategory: 'setCategory',
  SetAccount: 'setAccount',
  AddTags: 'addTags',
  RemoveTags: 'removeTags',
} as const;
export type BulkAction = (typeof BulkAction)[keyof typeof BulkAction];

/**
 * `PATCH /api/transactions/bulk` —— 批量改分类 / 改账户 / 加标签 / 去标签。
 *
 * ⚠️ `categoryId: null` 是**有效入参**（= 批量清空分类），故用 `nullish()` 而非 `optional()`
 * —— 前者既能接 `undefined`（没传）也能接 `null`（显式清空）。
 */
export const BulkPatchRequestSchema = z.object({
  ids: IdList,
  action: z.enum([BulkAction.SetCategory, BulkAction.SetAccount, BulkAction.AddTags, BulkAction.RemoveTags]),
  /** `setCategory` 必填；`null` = 清空分类 */
  categoryId: z.string().trim().min(1).nullish(),
  /** `setAccount` 必填 */
  accountId: z.string().trim().min(1).nullish(),
  /** `addTags` / `removeTags` 必填 */
  tagIds: z.array(z.string().trim().min(1)).min(1, '至少要选 1 个标签').max(ListConfig.MaxTagsPerTransaction).optional(),
});
export type BulkPatchRequest = z.infer<typeof BulkPatchRequestSchema>;

/** `POST /api/transactions/bulk-delete` —— 批量软删（🔴 二次确认由前端做，服务端只认 id 集合） */
export const BulkDeleteRequestSchema = z.object({ ids: IdList });
export type BulkDeleteRequest = z.infer<typeof BulkDeleteRequestSchema>;

/** `POST /api/transactions/bulk-restore` —— 批量恢复（30 天内） */
export const BulkRestoreRequestSchema = z.object({ ids: IdList });
export type BulkRestoreRequest = z.infer<typeof BulkRestoreRequestSchema>;

// ═══════════════════════════════ 筛选（9 维） ═══════════════════════════════

/**
 * 逗号分隔 / 重复键**都接受**的列表解析。
 *
 * 两种写法在真实客户端里都会出现：`?accountIds=a,b`（拼接）与 `?accountIds=a&accountIds=b`（数组）。
 * 只认一种就会"某一端传什么都筛不出来"，且**不报错** —— 属最难查的一类静默失败。
 *
 * 归一化函数抽出来共用，避免"id 列表一种写法、枚举列表另一种写法"。
 */
function normalizeCsv(v: unknown): string[] | undefined {
  if (v === undefined) return undefined;
  const raw = Array.isArray(v) ? (v as unknown[]) : [v];
  const flat = raw
    .flatMap((s) => String(s).split(','))
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  // 全空串 ⇒ **视为未筛选**（`undefined`），而不是"筛一个空集合"——后者会静默返回 0 行
  return flat.length === 0 ? undefined : flat;
}

/** 字符串 id 列表（账户 / 成员 / 分类 / 标签共用） */
function csvIds() {
  return z.preprocess(normalizeCsv, z.array(z.string().trim().min(1, 'id 不得为空')).min(1).optional());
}

/** 枚举取值列表（方向 / 状态 / 来源共用） */
function csvEnums<const T extends readonly [string, ...string[]]>(values: T) {
  return z.preprocess(normalizeCsv, z.array(z.enum(values)).min(1).optional());
}

/** 关键词维度（第 10 维）的开区间下界 —— 「搜索默认**全时间**」（`AC-C3.5`） */
export const ALL_TIME_FROM = '0000-01-01' as const;

/**
 * `GET /api/transactions` 的筛选入参（**维度间 AND、维度内 OR** —— C 域 §5.1.4 末行）。
 *
 * ## 与分页参数的边界（架构 §6.4）
 * `page` / `pageSize` / `from` / `to`（= **维度 1「时间」**）由**统一分页中间件**解析，
 * 本 schema **不碰它们** —— 两处都能解析就会出现"以谁为准"的第二份实现。
 *
 * ## 🆕 维度 10「关键词」已补齐（C3 批，2026-09-30）
 * 它是 §5.3 的搜索框（**按输入形态分流**：纯数字→金额 / 日期形态→日期范围 / 其余→跨
 * 备注·标签·分类名·账户名·成员名的包含匹配），分流规则由 `nucleus/search.ts` 的
 * `planSearch()` **唯一实现**。⇒ 本批筛选维度 **10 / 10**，`AC-C1.13` 的完整验收已具备。
 *
 * ⚠️ **`keyword` 的"默认全时间"与维度 1 的关系**（`AC-C3.5`）：搜索**不随**默认近 3 月窗口收窄
 * —— 搜索即找老账。该行为由**服务端在检测到 `keyword` 时把窗口放宽到全时间**实现
 * （契约层不表达，因为它要改的是分页窗口而不是一个筛选值）。
 */
export const TransactionQuerySchema = z.object({
  /** 维度 2 账户（多选 OR） */
  accountIds: csvIds().optional(),
  /** 维度 3 成员（多选 OR）—— 🔴 = **记录人 `transactions.userId`**（见下方「成员」说明） */
  memberIds: csvIds().optional(),
  /** 维度 4 分类（两级树，**选 L1 自动含 L2** —— 展开在服务端做，客户端只传所选 id） */
  categoryIds: csvIds().optional(),
  /** 维度 5 标签（多选 OR，含系统标签） */
  tagIds: csvIds().optional(),
  /** 维度 6 金额区间 —— 🔴 **按账户币种分别生效**（原币整数分，不折算比较） */
  amountMin: z.coerce.number().int().min(0).optional(),
  amountMax: z.coerce.number().int().min(0).optional(),
  /** 维度 7 方向（单选） */
  directions: csvEnums([
    TxnDirectionFilter.Income,
    TxnDirectionFilter.Expense,
    TxnDirectionFilter.Transfer,
    TxnDirectionFilter.Excluded,
  ]).optional(),
  /** 维度 8 状态（多选 OR；`deleted` 语义特殊 —— 见 {@link TxnStatusFilter}） */
  statuses: csvEnums([
    TxnStatusFilter.Uncategorized,
    TxnStatusFilter.Unpaid,
    TxnStatusFilter.Refund,
    TxnStatusFilter.Deleted,
  ]).optional(),
  /** 维度 9 来源（多选 OR） */
  sources: csvEnums([
    TxnSourceFilter.Manual,
    TxnSourceFilter.Import,
    TxnSourceFilter.Schedule,
    TxnSourceFilter.Ai,
  ]).optional(),
  /** 维度 10 关键词（= §5.3 的搜索框；分流规则见 `nucleus/search.ts`） */
  keyword: z.string().trim().max(TransactionPolicy.KeywordMaxLength, `关键词不超过 ${TransactionPolicy.KeywordMaxLength} 字符`).optional(),
});
export type TransactionQuery = z.infer<typeof TransactionQuerySchema>;

// ═══════════════════════════════ 导出 ═══════════════════════════════

/** 导出格式（C 域 PRD §5.1.11：**CSV + JSON** 两种，永久免费） */
export const ExportFormat = { Csv: 'csv', Json: 'json' } as const;
export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

/**
 * `GET /api/transactions/export` 的查询参数 = **同一套筛选 + 格式 + 范围开关**。
 *
 * 🔴 **刻意复用 {@link TransactionQuerySchema}**（而不是另立一份导出筛选）：
 * 「导出 = 当前筛选结果」（§5.1.11 原文）+ `AC-C1.35` —— 若导出另有一套筛选，
 * 用户就会看到"屏幕上 12 笔、导出来 15 笔"。同时这也让**导出与列表共用同一份
 * `buildWhere()`**（`AC-C1.35` 的机理保障）。
 *
 * ⚠️ **导出不分页**（`exportAll`）：导出是"把当前筛选结果全拿走"，
 * 分页会让用户拿到半个文件而不自知。上限由 `TransactionPolicy.MaxExportRows` 兜底。
 */
export const ExportTransactionsQuerySchema = TransactionQuerySchema.extend({
  format: z.enum([ExportFormat.Csv, ExportFormat.Json]).default(ExportFormat.Csv),
  /**
   * 是否忽略默认时间窗导出**全部时间**。
   * ⚠️ **`keyword` 出现时无论本值如何都按全时间导出**（与搜索口径一致，`AC-C3.5`）。
   */
  exportAll: z.enum(['true', 'false']).optional(),
});
export type ExportTransactionsQuery = z.infer<typeof ExportTransactionsQuerySchema>;

/** 导出响应的元信息（放在响应头，便于端侧取文件名） */
export const ExportHeaders = {
  /** 文件名（端侧 `Content-Disposition` 的 `filename*` 取值） */
  FileName: 'x-export-file-name',
} as const;

/**
 * ⚠️ **「成员」维度 = 记录人（`transactions.userId`），不是账户归属人。**
 *
 * 依据：L 域 PRD **§5.3-6**（「贡献视图的『记录支出』= **按 `userId` 分组**」）与
 * 架构 **§4.4**（`transactions (ledgerId, userId)` 索引的用途写的是「首屏 / **贡献视图** / 拆分 …」）。
 * ⇒ 贡献视图与流水筛选**必须用同一个"成员"定义**，否则"同一个人"在两个页面看起来不是同一个人。
 *
 * ⚠️ **一处待裁（本批登记）**：L 域 PRD §5.3-6 又说「**共同（Ours）** = `userId` 为空的系统生成流水」，
 * 而冻结后的 schema 里 `transactions.userId` 是 **NOT NULL**（架构 §4.2）⇒ 该定义**当前不可实现**。
 * 属 L 域 ③（贡献视图）的前置口径，已登记进待决策清单（**J-8**），本批不擅自补字段。
 */

// ═══════════════════════════════ 视图 ═══════════════════════════════

/** 一条腿的对外形态（含 join 出来的账户名 / 分类名 —— 端侧零 join） */
export interface TransactionLegView {
  id: string;
  accountId: string;
  accountName: string;
  /**
   * 腿账户的**归属人**（`accounts.ownerId`）—— 🔴 与「记录人」是两回事（`DR-018` §3.2）。
   *
   * 为什么放在腿视图上（2026-10-06 · 流水页批次）：抽屉要显示「归属人」（`AC-C1.33` 的
   * 成员一行 · `AC-L3.5` 的归因三行），而归属人**只能由 `legs → accounts.ownerId` 得到**。
   * 本视图的既定原则就是"**端侧零 join**"（见上面 `accountName` / `categoryName` 的注释）
   * ⇒ 归属人也必须**服务端算好下发**，否则 Web 与小程序各写一遍 `accountId → owner` 的映射
   * 就成了同一事实的两份实现（铁律 24）。
   */
  ownerId: string;
  /** 归属人展示名（`users.name`） */
  ownerName: string;
  /** 腿币种 = **账户币种**（腿不挂币种列） */
  currency: string;
  direction: LegDirection;
  /** **整数分（原币）** —— 展示原币，不折算（`DR-004` / `AC-C1.31`） */
  amount: number;
  /**
   * 🔴 **笔级优惠（整数分 · 原币）** —— 腿的 `amount` 是**实付净额**，「原价」= `amount + discount`
   * 由端侧**派生**（不落库、不下发原价）。依据 `DR-040` / 规格 §3.5；缺省 `0`。
   */
  discount: number;
  /**
   * 🆕 **重大资产归因**（R 域 `DR-053` D12 · 批 91）。
   *
   * `null` = 与资产无关。R 域的回报四数**由服务端聚合**，本字段在流水详情里只用于
   * "这笔钱挂在哪项资产上"的展示 —— 🔴 端侧**不得**据此自己累加（`AC-R7.2`）。
   */
  linkedMajorAssetId: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryKind: CategoryKind | null;
  note: string | null;
  excludeFromStats: boolean;
}

/**
 * 列表行（= C 域 §5.1.1 的「显示单元 = 交易」）。
 *
 * 🔴 `parent` 是**派生量**（`deriveParentRow`），库里没有对应字段（`AC-C1.3`）。
 * 🔴 三个 `has*` / `source` 也是派生量 —— 由服务端算好下发，**端侧不自行判定**，
 * 否则「什么算退款」会在 Web 与小程序各有一份答案。
 */
/**
 * 🆕 批 `153`：**「这笔没有分类，但它是什么业务」** —— 分类列**文字**兜底的依据。
 *
 * ## 🔴 为什么由服务端判
 * 「信用卡还款」「投资买入 / 卖出」是**账户类型 × 腿方向**的组合事实，
 * 而腿视图里没有 `accountType` ⇒ 端侧要判就得再拿一份账户表（= 第二份口径）。
 *
 * ## 与 `ParentRowKind` 的分工
 * `kind` 回答「净额怎么取」（单腿 / 多腿净额 / 转账）；本枚举回答「**展示上它是什么**」。
 * 二者**不等价**：信用卡还款在全腿不计收支时 `kind = transfer`，
 * 但用户要看到的是「信用卡还款」，不是「转账」。
 *
 * 🔴 端侧**只在该笔没有任何分类名时**才用它（有分类就显示分类）。
 */
export const ParentRowBusinessKind = {
  /** 转账（跨账户资金搬运） */
  Transfer: 'transfer',
  /** 信用卡还款（信用卡 `in` 腿 + 其它账户 `out` 腿） */
  CreditRepayment: 'creditRepayment',
  /** 投资买入（投资账户在 `in` 侧） */
  InvestmentBuy: 'investmentBuy',
  /** 投资卖出（投资账户在 `out` 侧） */
  InvestmentSell: 'investmentSell',
} as const;
export type ParentRowBusinessKind =
  (typeof ParentRowBusinessKind)[keyof typeof ParentRowBusinessKind];

/**
 * 服务端下发的父行视图 = 内核派生（`ParentRow`）+ 展示用注记。
 *
 * 🆕 批 `153`：`businessKind` 是**可选**字段 —— 老响应 / 手写夹具没有它时，
 * 端侧一律按「支出 / 收入」兜底（不抛、也不留空白）。
 */
export type ParentRowView = ParentRow & {
  businessKind?: ParentRowBusinessKind | null;
};

/**
 * 🆕 一笔消费**被退款**的汇总（批 `120` · `DR-069` D7）。
 *
 * 服务端一次 `GROUP BY parent_id` 算出（禁 N+1 —— 首屏 800 行 / 压测 15000，`DR-006`）。
 * 🔴 只统计**未删除**的退款交易（用户撤销一次退款，额度要还回去）。
 */
export interface TransactionRefundSummary {
  /** 挂在它下面的退款**笔数**（支持多次部分退款 ⇒ 可能是 2、3…） */
  count: number;
  /** 这些退款的**冲减腿**金额合计（正数，原币整数分） */
  amount: number;
  /**
   * 🆕 批 `154`：按**原支出分类**的已退明细（`categoryId → 已退原币整数分`）。
   *
   * ## 为什么要有它
   * 拆分成多项的一笔支出要支持「**只退其中一项**」—— 端侧要回答"这一项还能退多少"，
   * 而 `amount` 是**整笔**的合计：拿它算单项剩余，会把别的项的已退额也算进来。
   *
   * 🔴 聚合口径与 `amount` **同一条**（`loadRefundTotals` 一次查询的两个维度），
   * 不是第二份实现；不带分类的冲减腿**不出键**（它们不受分类上限约束）。
   */
  byCategory?: Record<string, number>;
}

/**
 * 🆕 退款行的**父摘要**（批 `120` · `DR-069` D5）。
 *
 * 原来只下发 `parentDeleted`（一个布尔），端侧只能说"原交易已删除"，
 * 却**说不出冲的是哪一笔** —— 列表上退款行与普通收入行完全同形。
 */
export interface TransactionParentBrief {
  id: string;
  /** 原交易备注（可能为空 —— 端侧回退到分类名或「（无备注）」） */
  note: string | null;
  /** 原交易日 `YYYY-MM-DD` */
  date: string;
}

export interface TransactionRowView {
  id: string;
  /** `YYYY-MM-DD` */
  date: string;
  note: string | null;
  /** 记录人 */
  userId: string;
  /** 记录人展示名（`users.name`） */
  userName: string;
  status: TransactionStatus;
  excludeFromStats: boolean;
  /**
   * 🔴 **父交易 id**（`transactions.parentId` 的**事实值**，非派生）。
   *
   * 🔴 **`DR-035` v1.1 改判后只剩一个用途 = 退款**（本笔冲减的是哪一笔）——
   * 见 `CreateTransactionRequestSchema.parentId` 的说明表。
   */
  parentId: string | null;
  /**
   * 🔴 **父交易是否已被软删**（派生量）：
   * - `null` = 本笔没有父；
   * - `true` = 父已软删（或已被物理清理）⇒ 前端显示「**原交易已删除**」（`AC-C2.15`）；
   * - `false` = 父在。
   *
   * ⚠️ 为什么必须由服务端下发：`AC-C2.15` 要求"原交易被删时**退款腿保留并标注**"，
   * 而"父还在不在"要再查一次表 —— 端侧各查一遍就是同一事实的第二份实现（铁律 24）。
   */
  parentDeleted: boolean | null;
  /**
   * 🔴 派生父行（金额 / 方向 / 转账两端 / 是否退化）—— 🆕 批 `153` 起多带一个
   * `businessKind`（无分类时的业务文字依据，见 {@link ParentRowBusinessKind}）。
   */
  parent: ParentRowView;
  /**
   * 🆕 **本笔被退款的汇总**（批 `120` · `DR-069`）。
   *
   * | 取值 | 含义 |
   * |---|---|
   * | `{count, amount}` | 本笔消费被退过（可多次部分退款） |
   * | `null` | 没有被退过 —— **包括"本笔自己就是退款"**（退款不能再被退） |
   *
   * 🔴 行上显示的金额 = `netAfterRefund(parent.net, refunds.amount)`（内核唯一实现）。
   * 🔴 它**不改变** `parent.net`（`net` 仍是对账 / 可退额度 / 导出用的绝对事实）。
   */
  refunds: TransactionRefundSummary | null;
  /**
   * 🆕 **退款行的父摘要**（批 `120` · `DR-069` D5）：本笔冲减的是哪一笔。
   * 普通记账 ⇒ `null`。
   */
  parentBrief: TransactionParentBrief | null;
  /** 全部腿（展开行直接渲染，不再发第二次请求） */
  legs: TransactionLegView[];
  tagIds: string[];
  tagNames: string[];
  /** 维度 9 的**实测值** */
  source: TxnSourceFilter;
  /** 是否含**退款冲减腿**（`in` 腿 + 支出分类）—— 维度 8「只看退款」的同一判据 */
  hasRefundLeg: boolean;
  /** 是否含**未分类的计入收支腿** —— 维度 8「未分类」的同一判据 */
  hasUncategorizedLeg: boolean;
  createdAt: number;
  updatedAt: number;
  /** 软删时刻（UTC 毫秒）；`null` = 未删 */
  deletedAt: number | null;
  /**
   * 🆕 **这笔消费被哪个分期计划覆盖**（`DR-067` · 批 `116`）。
   *
   * | 取值 | 含义 |
   * |---|---|
   * | `string` | 「记账时分期」刚建出的计划 id（**只有那条路径下发**） |
   * | `undefined` | 其它路径（列表 / 详情 / 历史数据）—— 它们不查这张表 |
   * | `null` | 明确「本笔没有分期」的场合 |
   *
   * 🔴 流水页的「已分 N 期」chip **不读本字段**：它按 `GET /api/installments` 的 `sources`
   * 统一 join（一处判据）。本字段只服务"记账成功后立刻给出分期入口"这一处。
   */
  installmentPlanId?: string | null;
}

/** `GET /api/transactions/:id` —— 详情（抽屉用）。 = 行视图 + 标签对象（抽屉要显示标签而非只有名字） */
export interface TransactionDetailView extends TransactionRowView {
  tags: TransactionTagView[];
  /**
   * 🆕 **归因三行**（`AC-C1.33` 的「成员」一行 · `AC-L3.5`）—— **服务端算好下发**。
   *
   * 🔴 为什么不在端侧由 `legs[].accountId` 自己映射归属人：
   * 归属人的载体是 `accounts.ownerId`，而"一笔交易涉及哪些归属人"要**跨腿去重**
   * （多腿交易可能分属不同归属人的账户）⇒ 端侧各写一遍 = 同一事实两份实现（铁律 24）。
   * `AttributionView` 早在 ㊻ 批就**为此预留**（`contract/contribution.ts`），本批首次实装。
   *
   * ⚠️ 与之配套，{@link TransactionLegView} 也补了 `ownerId` / `ownerName` ——
   * 那是**逐腿**的归属（抽屉里逐腿显示用），本字段是**整笔去重后**的归属（三行显示用）。
   * 两者同源（都来自 `accounts.ownerId`），不是两套判据。
   */
  attribution: AttributionView;
  /**
   * 🆕 工资条录入口径（批 141）：`'simple'` = 简化记账（未含社保与个税）；
   * 普通交易 / **完整工资条**为 `null`（完整工资条的结构本身就是事实）。
   */
  salaryMode: SalaryMode | null;
}

/**
 * `GET /api/transactions/last-salary` —— **上一笔工资条**（批 141 · 「沿用上月」的数据源）
 *
 * ## 🔴 为什么由服务端挑、端侧折叠
 *
 * 「哪一笔算工资条」是**跨表判据**（腿 + 分类白名单），端侧只有列表摘要、拿不到腿 ⇒
 * 服务端复用 `nucleus/payday.ts` 的 `recognizeSalary()`（与 F3 发薪日、工资条识别**同一份内核**）
 * 挑出最近一笔。
 *
 * 但**「腿 → 子项」的折叠不在这里**：那是端侧录入层的形态（`salaryItemsFromLegs()`），
 * 服务端再写一遍就是同一件事的两份实现（铁律 24）。⇒ 本接口只回腿，折叠交给端侧。
 *
 * ## 为什么回整笔腿而不是"结构摘要"
 *
 * 摘要 = 服务端先折叠一次 ⇒ 立刻变成第二份折叠实现，且与端侧的配对规则一有偏差
 * 就会出现"沿用上月的结构和我打开上个月看到的不一样"。
 */
export interface LastSalaryView {
  readonly transactionId: string;
  /** `YYYY-MM-DD` */
  readonly date: string;
  /** 该笔的全部腿（**原样**，端侧自行折叠） */
  readonly legs: TransactionLegView[];
}

/**
 * `GET /api/transactions/last-salary` 的**外层**。
 *
 * 🔴 为什么包一层而不是直接返回 `LastSalaryView | null`：
 * Nest 对 `null` 返回值会发**空 body**（不是字面量 `null`）⇒ 端侧 `JSON.parse('')` 直接抛。
 * 包成 `{ item: null }` 后，「没有可沿用的工资条」是一个**正常结果**，不是解析失败。
 */
export interface LastSalaryResult {
  /** `null` = 这个账本还没有可沿用的工资条（不是错误） */
  readonly item: LastSalaryView | null;
}

/** 标签的轻量形态（详情抽屉内嵌用；完整标签管理见 `contract/tag.ts`） */
export interface TransactionTagView {
  id: string;
  name: string;
  system: boolean;
}

/** 批量动作的结果 —— 🔴 **被拒的逐条带原因**（`AC-C1.21` 禁止静默失败） */
export interface BulkPatchResultView {
  action: BulkAction;
  /** 请求里选中的笔数 */
  requested: number;
  /** 实际改动笔数 */
  applied: number;
  /** 被拒明细（如"仅单腿交易支持批量改账户"） */
  rejected: { id: string; reason: string }[];
}

/**
 * 批量删除 / 恢复的结果。
 *
 * 🔴 **恢复时多一个 `consistency` 字段**（`AC-C1.27`）—— 删除不需要它（删除不会"恢复"任何东西），
 * 但两处共用同一响应类型，故设为可选。恢复路径**恒带**它。
 */
export interface BulkMutationResultView {
  requested: number;
  affected: number;
  /** 已在目标状态、本次未变动的笔数（幂等友好：重复点是同一件事，不报错） */
  skipped: number;
  /**
   * 🔴 **恢复一致性检查结果**（`AC-C1.27`）—— **只有恢复路径有**（删除时为 `undefined`）。
   *
   * ⚠️ 依据 PRD §5.1.6「恢复时**校验余额 / 账单一致性**」原文是「冲突项**列出提示**」
   * ⇒ **本字段只用于提示，不拦截恢复**（`affected` 照常 +1）。端侧据此在回收站里
   * 弹一条"这次恢复让某账户变成了负数"之类的提醒，但**钱已经回来了**。
   */
  consistency?: RestoreConsistencyView;
}

/**
 * 恢复一致性结果（`AC-C1.27`）—— 契约层**转发**共享内核的形状。
 *
 * 🔴 端侧**不得**自己算：判定唯一源 = `nucleus/restore.ts` 的 `checkRestoreConsistency()`。
 * 本类型是它的**传输层投影**（同 `NetWorthResponse` 转发 `NetWorthResult` 的做法）。
 */
export interface RestoreConsistencyView {
  /** 冲突清单（含 `notChecked` 未检查项；可能只有未检查项） */
  conflicts: readonly RestoreConsistencyConflictView[];
  /** `= 存在 kind !== 'notChecked' 的项`；UI 用它决定是否高亮 */
  hasConflict: boolean;
  /**
   * 🔴 **未检查的校验项**（未落地域）—— 恒非空直到 M/Q/I 域落地。
   * 与 `conflicts` 里 `kind === 'notChecked'` 项**同源**（不各写一遍文案）。
   */
  notChecked: readonly string[];
}

export interface RestoreConsistencyConflictView {
  /** `wouldGoNegative` / `wouldFlipSign` / `notChecked` */
  kind: string;
  /** 归到哪个账户（`notChecked` 时**不传** —— 它是全局的） */
  accountId?: string;
  accountLabel?: string;
  /** 中文提示（唯一源在内核，端侧不得各写一份） */
  message: string;
}
